import { useEffect, useRef, useState } from "react";
import { useUploadStore } from "@components/FileUpload/uploadStore";
import FormItem from "@components/FormItem/FormItem";
import { getPortServiceCategoryLabel } from "@features/vessel-call/vessel-call-status/PortServices/preview/utils";
import { useFormattedReference } from "@hooks/useFormattedReference";
import type {
  PortServiceCategory,
  PortServiceSubtype,
  PortServiceType,
} from "@services/reference-data/types";
import {
  getPortServiceSubtypes,
  getPortServiceTypes,
  REFERENCE_DATA_TYPES,
} from "@services/referenceDataApi";
import { useQuery } from "@tanstack/react-query";
import { startFieldNavigationTiming } from "@telemetry/fieldNavigationTiming";
import { useTrackPendingFieldNavigation } from "@telemetry/trackPendingFieldNavigation";
import { t } from "i18next";
import { FormProvider, type UseFormReturn } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import type { PortServicesFormData } from "../schema/portServicesFormSchema";
import { usePortServicesFormStore } from "../store/usePortServicesForm.store";
import { isShipToShipService } from "./utils";

type AddPortServiceOutlet = {
  formData: UseFormReturn<PortServicesFormData>;
  updateFormData: (key: string, value: unknown) => void;
  handleSubmit: () => void;
  cancel: () => void;
};

const PORT_SERVICE_DOCUMENT_FIELDS: Array<keyof PortServicesFormData> = [
  "attachmentDocuments",
  "garbageDocument",
  "slopsDocument",
  "sludgeDocument",
  "debunkeringDocument",
];

interface SelectPortServiceProps {
  langBase: string;
  serviceCategoryValue: string;
  serviceTypeValue: string;
}
const findPortServiceLabelByCode = (
  serviceList: any[],
  serviceCodeValue: any,
  serviceCodeKey: string,
) => serviceList.find((service) => service[serviceCodeKey] === serviceCodeValue) || {};

const SERVICE_TYPES_FIELD_NAVIGATION_ID = "port-services.service-category.service-types";
const SERVICE_SUBTYPES_FIELD_NAVIGATION_ID = "port-services.service-type.service-subtypes";
const SERVICE_TYPES_FIELD_NAVIGATION_CONTEXT = {
  module: "vessel_call",
  screen: "Add Port Service",
  action: "load_port_service_types",
  fromField: "serviceCategory",
  toField: "serviceType",
  entityType: "vessel_call",
};
const SERVICE_SUBTYPES_FIELD_NAVIGATION_CONTEXT = {
  module: "vessel_call",
  screen: "Add Port Service",
  action: "load_port_service_subtypes",
  fromField: "serviceType",
  toField: "serviceSubType",
  entityType: "vessel_call",
};

const SelectPortService: React.FC<SelectPortServiceProps> = ({
  langBase,
  serviceCategoryValue,
  serviceTypeValue,
}) => {
  const { formData, updateFormData } = useOutletContext<AddPortServiceOutlet>();
  const { getField, setField, isSubtypeMandatory, isTypeMandatory } = usePortServicesFormStore();
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const [categoryCode, setCategoryCode] = useState<string | undefined>();
  const [typeCode, setTypeCode] = useState<string | undefined>();

  const prevServiceCategoryRef = useRef<string | null>(null);
  const prevServiceTypeRef = useRef<string | null>(null);
  const isShipToShipCategory = isShipToShipService(serviceCategoryValue);

  const isServiceTypeDisabled = !serviceCategoryValue;
  const isServiceSubTypeDisabled = !serviceTypeValue;

  const { options: portServiceCategories } = useFormattedReference<PortServiceCategory>(
    REFERENCE_DATA_TYPES.PORT_SERVICE_CATEGORIES,
    "bff-vessel-call",
  );
  const {
    data: portServiceTypes = [],
    isFetching: isPortServiceTypesFetching,
    isLoading: isPortServiceTypesLoading,
    isError: isPortServiceTypesError,
    isSuccess: isPortServiceTypesSuccess,
    error: portServiceTypesError,
    dataUpdatedAt: portServiceTypesUpdatedAt,
  } = useQuery({
    queryKey: ["portServiceTypes", categoryCode],
    queryFn: () => getPortServiceTypes("bff-vessel-call", categoryCode!),
    enabled: !!categoryCode,
  });

  const {
    data: portServiceSubtypes = [],
    isFetching: isPortServiceSubtypesFetching,
    isLoading: isPortServiceSubtypesLoading,
    isError: isPortServiceSubtypesError,
    isSuccess: isPortServiceSubtypesSuccess,
    error: portServiceSubtypesError,
    dataUpdatedAt: portServiceSubtypesUpdatedAt,
  } = useQuery({
    queryKey: ["portServiceSubtypes", typeCode],
    queryFn: () => getPortServiceSubtypes("bff-vessel-call", typeCode!),
    enabled: !!typeCode,
  });
  const shouldHideServiceType = isShipToShipCategory && portServiceTypes.length === 1;

  useTrackPendingFieldNavigation(SERVICE_TYPES_FIELD_NAVIGATION_ID, {
    isFetching: isPortServiceTypesFetching,
    isLoading: isPortServiceTypesLoading,
    isError: isPortServiceTypesError,
    isSuccess: isPortServiceTypesSuccess,
    error: portServiceTypesError,
    dataUpdatedAt: portServiceTypesUpdatedAt,
  });

  useTrackPendingFieldNavigation(SERVICE_SUBTYPES_FIELD_NAVIGATION_ID, {
    isFetching: isPortServiceSubtypesFetching,
    isLoading: isPortServiceSubtypesLoading,
    isError: isPortServiceSubtypesError,
    isSuccess: isPortServiceSubtypesSuccess,
    error: portServiceSubtypesError,
    dataUpdatedAt: portServiceSubtypesUpdatedAt,
  });

  useEffect(() => {
    if (!serviceCategoryValue) {
      setCategoryCode("");
      return;
    }

    if (serviceCategoryValue && portServiceCategories.length) {
      const { portServiceCategoryCode } = findPortServiceLabelByCode(
        portServiceCategories,
        serviceCategoryValue,
        "portServiceCategoryDesc",
      );
      setCategoryCode(portServiceCategoryCode);
    }
  }, [serviceCategoryValue, portServiceCategories]);

  useEffect(() => {
    if (!serviceTypeValue) {
      setTypeCode("");
      return;
    }

    if (serviceTypeValue && portServiceTypes.length) {
      const { portServiceTypeCode } = findPortServiceLabelByCode(
        portServiceTypes,
        serviceTypeValue,
        "portServiceTypeDesc",
      );
      setTypeCode(portServiceTypeCode);
    }
  }, [serviceCategoryValue, serviceTypeValue, portServiceTypes]);

  useEffect(() => {
    if (!shouldHideServiceType || !portServiceTypes.length) {
      return;
    }

    const soleType = portServiceTypes[0];
    if (!soleType?.portServiceTypeDesc) {
      return;
    }

    if (serviceTypeValue !== soleType.portServiceTypeDesc) {
      formData.setValue("serviceType", soleType.portServiceTypeDesc, {
        shouldDirty: true,
        shouldValidate: false,
      });
      updateFormData("serviceType", soleType.portServiceTypeDesc);
    }

    const currentServiceTypeCode = getField("serviceTypeCode");

    if (soleType.portServiceTypeCode && currentServiceTypeCode !== soleType.portServiceTypeCode) {
      setField("serviceTypeCode", soleType.portServiceTypeCode);
    }

    if (soleType.portServiceTypeCode && typeCode !== soleType.portServiceTypeCode) {
      setTypeCode(soleType.portServiceTypeCode);
    }
  }, [
    formData,
    getField,
    portServiceTypes,
    serviceTypeValue,
    setField,
    shouldHideServiceType,
    typeCode,
    updateFormData,
  ]);

  useEffect(() => {
    const prev = prevServiceCategoryRef.current;
    if (prev !== null && prev !== serviceCategoryValue) {
      formData.setValue("serviceType", "");
      formData.setValue("serviceSubType", "");
      setTypeCode("");
      setField("serviceTypeCode", "");
      setField("serviceSubTypeCode", "");
      PORT_SERVICE_DOCUMENT_FIELDS.forEach((fieldName) => {
        updateFormData(fieldName, []);
        clearFilesForKey(fieldName);
      });
    }

    prevServiceCategoryRef.current = serviceCategoryValue;
  }, [clearFilesForKey, formData, serviceCategoryValue, setField, updateFormData]);

  // When serviceType changes, clear subtype
  useEffect(() => {
    if (serviceTypeValue) {
      const prev = prevServiceTypeRef.current;

      if (prev !== null && prev !== serviceTypeValue) {
        formData.setValue("serviceSubType", "");
        setField("serviceSubTypeCode", "");
      }

      prevServiceTypeRef.current = serviceTypeValue;
    }
  }, [formData, serviceTypeValue, setField]);

  useEffect(() => {
    if (!categoryCode) return;

    const nextSubype = portServiceSubtypes.length > 0;
    const nextType = portServiceTypes.length > 0;
    if (isSubtypeMandatory !== nextSubype) {
      setField("isSubtypeMandatory", nextSubype);
    }

    if (isTypeMandatory !== nextType) {
      setField("isTypeMandatory", nextType);
    }
  }, [
    portServiceTypes,
    portServiceSubtypes,
    categoryCode,
    isSubtypeMandatory,
    isTypeMandatory,
    setField,
  ]);

  const portServiceCategoriesOptions = portServiceCategories.map(
    ({ portServiceCategoryDesc }: PortServiceCategory) => ({
      label: getPortServiceCategoryLabel(portServiceCategoryDesc),
      value: portServiceCategoryDesc,
    }),
  );
  const portServiceTypesOptions = portServiceTypes.map(
    ({ portServiceTypeDesc }: PortServiceType) => ({
      label: portServiceTypeDesc,
      value: portServiceTypeDesc,
    }),
  );

  const portServiceSubtypesOptions = portServiceSubtypes.map(
    ({ portServiceSubtypeDesc }: PortServiceSubtype) => ({
      label: portServiceSubtypeDesc,
      value: portServiceSubtypeDesc,
    }),
  );
  return (
    <FormProvider {...formData}>
      <div className="grid grid-cols-2">
        <FormItem<PortServicesFormData, "serviceCategory">
          name="serviceCategory"
          type="select"
          label={t(`${langBase}.fields.service-category`)}
          testId="portServices-serviceCategory"
          onFieldChange={(key, value) => {
            updateFormData(key, value);
            const { portServiceCategoryCode } = findPortServiceLabelByCode(
              portServiceCategories,
              value,
              "portServiceCategoryDesc",
            );
            if (portServiceCategoryCode) {
              startFieldNavigationTiming(
                SERVICE_TYPES_FIELD_NAVIGATION_ID,
                SERVICE_TYPES_FIELD_NAVIGATION_CONTEXT,
              );
            }
            setField("serviceCategoryCode", portServiceCategoryCode);
            setField("serviceTypeCode", "");
            setField("serviceSubTypeCode", "");
          }}
          options={portServiceCategoriesOptions}
        />
      </div>
      {!!portServiceTypes.length && !shouldHideServiceType && (
        <div className="grid grid-cols-2">
          <FormItem<PortServicesFormData, "serviceType">
            name="serviceType"
            type="select"
            disabled={isServiceTypeDisabled}
            label={t(`${langBase}.fields.service-type`)}
            testId="portServices-serviceType"
            onFieldChange={(key, value) => {
              updateFormData(key, value);
              const { portServiceTypeCode } = findPortServiceLabelByCode(
                portServiceTypes,
                value,
                "portServiceTypeDesc",
              );
              if (portServiceTypeCode) {
                startFieldNavigationTiming(
                  SERVICE_SUBTYPES_FIELD_NAVIGATION_ID,
                  SERVICE_SUBTYPES_FIELD_NAVIGATION_CONTEXT,
                );
              }
              setField("serviceTypeCode", portServiceTypeCode);
              setField("serviceSubTypeCode", "");
            }}
            options={portServiceTypesOptions}
          />
        </div>
      )}
      {!!portServiceSubtypes.length && (
        <div className="grid grid-cols-2">
          <FormItem<PortServicesFormData, "serviceSubType">
            name="serviceSubType"
            type="select"
            disabled={isServiceSubTypeDisabled}
            label={t(`${langBase}.fields.service-subtype`)}
            testId="portServices-serviceSubType"
            onFieldChange={(key, value) => {
              updateFormData(key, value);
              const { portServiceSubtypeCode } = findPortServiceLabelByCode(
                portServiceSubtypes,
                value,
                "portServiceSubtypeDesc",
              );
              setField("serviceSubTypeCode", portServiceSubtypeCode);
            }}
            options={portServiceSubtypesOptions}
          />
        </div>
      )}
    </FormProvider>
  );
};

export default SelectPortService;
